import { createAsyncThunk } from "@reduxjs/toolkit";
import request from "../../../../service";
import { deletedSubjects, setSubjectsData, setSubjectsRestoreData, updateSubjects } from "./subjectSlice";
import { IErrorResponse, IRootState } from "../../../../types";
import { IChildSubjectPayload, IGetSubjects, ISubjectChildFormData, ISubjectCreate, ISubjectPayload } from "../types";

const GetSubjects = createAsyncThunk<any, IGetSubjects | undefined>(
    "get/subjects",
    async (_data, { dispatch, rejectWithValue, getState }) => {
        const data: IGetSubjects = _data || {};
        const search = data.searchQuery;
        const page = data.page || 1;

        try {
            const response = await request.get("/subjects", {
                params: {
                    page_size: 10,
                    page,
                    ...(search ? { search } : {}),
                },
            });

            const lang = getState() as IRootState;
            const defaultLanguage = lang.languages.defaultLanguage;

            dispatch(
                setSubjectsData({
                    data: response.data.data,
                    defaultLanguage,
                    pagination: response.data.pagination,
                })
            );

            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);
const CreateSubjects = createAsyncThunk("create/subjects", async (data: ISubjectCreate, { rejectWithValue }) => {
    try {
        const response = await request.post("/subjects", data);
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const CreateChildSubject = createAsyncThunk(
    "create/subject/child",
    async (data: ISubjectChildFormData, { rejectWithValue }) => {
        try {
            const response = await request.post("/child-subjects", data);
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const EditSubjects = createAsyncThunk(
    "edit/editSubjects",
    async (data: ISubjectPayload, { dispatch, rejectWithValue }) => {
        try {
            const response = await request.put(`/subjects/${data.id}`, data);
            dispatch(updateSubjects(data));
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const EditSubjectChild = createAsyncThunk(
    "edit/subject/child",
    async (data: IChildSubjectPayload, { rejectWithValue }) => {
        try {
            const response = await request.put(`/child-subjects/${data.id}`, data);
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const DeleteSubjects = createAsyncThunk("delete/subjects", async (id: number, { dispatch, rejectWithValue }) => {
    try {
        const response = await request.delete(`subjects/${id}`);

        dispatch(deletedSubjects(id));
        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const DeleteSubjectsChild = createAsyncThunk("delete/subjects/child", async (id: number, { rejectWithValue }) => {
    try {
        const response = await request.delete(`child-subjects/${id}`);

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const GetDeletedSubjects = createAsyncThunk("get/deleted/subjects", async (_, { dispatch, rejectWithValue }) => {
    try {
        const response = await request.get(`/subjects/deleted-items`);
        dispatch(setSubjectsRestoreData(response.data));

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const RestoreDeleteSubjects = createAsyncThunk("restore/delete/subjects", async (id: number, { rejectWithValue }) => {
    try {
        const response = await request.post(`/subjects/restore/${id}`);

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const RestoreDeleteChildSubjects = createAsyncThunk(
    "restore/delete/child/subjects",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await request.post(`/child-subjects/restore/${id}`);

            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

const GenerateClassifier = createAsyncThunk("generate/classifier", async (_, { rejectWithValue }) => {
    try {
        const response = await request.get("subjects/get-random-classifier");

        return response.data;
    } catch (error) {
        const typedError = error as IErrorResponse;
        return rejectWithValue(typedError.response.data.message || "unexpected_fail");
    }
});

const GenerateChildClassifier = createAsyncThunk(
    "generate/child/classifier",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await request.get(`child-subjects/get-random-classifier/${id}`);
            return response.data;
        } catch (error) {
            const typedError = error as IErrorResponse;
            return rejectWithValue(typedError.response.data.message || "unexpected_fail");
        }
    }
);

export {
    GetSubjects,
    CreateSubjects,
    EditSubjects,
    DeleteSubjects,
    CreateChildSubject,
    EditSubjectChild,
    DeleteSubjectsChild,
    GetDeletedSubjects,
    RestoreDeleteSubjects,
    RestoreDeleteChildSubjects,
    GenerateClassifier,
    GenerateChildClassifier,
};
